import React from "react";
import styled, { keyframes } from "styled-components";
import { useUser } from "../Login/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFire, faRobot, faBullhorn, faTrophy } from "@fortawesome/free-solid-svg-icons";

/* ── アニメーション定義 ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.05); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fireGlow = keyframes`
  0%, 100% { text-shadow: 0 0 10px rgba(255,69,0,0.6), 0 0 30px rgba(255,140,0,0.4); }
  50%      { text-shadow: 0 0 20px rgba(255,69,0,0.9), 0 0 50px rgba(255,140,0,0.6), 0 0 80px rgba(255,200,0,0.3); }
`;

/* ── スタイル ── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(-45deg, #0f0c29, #302b63, #24243e, #1a1a2e);
  background-size: 400% 400%;
  animation: ${gradientShift} 12s ease infinite;
  padding: 40px 16px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.4rem, 5vw, 2.6rem);
  font-weight: 900;
  text-align: center;
  color: #ff6a00;
  animation: ${fadeInUp} 0.8s ease both, ${fireGlow} 3s ease-in-out infinite;
  margin-bottom: 6px;
  letter-spacing: 0.05em;
`;

const SubTitle = styled.p`
  font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-bottom: 30px;
  animation: ${fadeInUp} 0.8s ease 0.2s both;
`;

/* ── 画像エリア ── */
const HeroImageWrapper = styled.div`
  width: 100%;
  max-width: 780px;
  margin-bottom: 24px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(255, 100, 0, 0.25);
  animation: ${fadeInUp} 0.8s ease 0.3s both;
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

/* ── 熱いキャッチコピー ── */
const CatchCopy = styled.p`
  font-size: clamp(1rem, 3vw, 1.3rem);
  font-weight: 700;
  color: #ffd700;
  text-align: center;
  line-height: 2;
  margin: 10px 0 36px;
  animation: ${fadeInUp} 0.8s ease 0.5s both, ${fireGlow} 4s ease-in-out 1s infinite;
  white-space: pre-wrap;
`;

/* ── カード ── */
const Card = styled.div`
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 28px 24px;
  max-width: 640px;
  width: 100%;
  margin-bottom: 20px;
  animation: ${fadeInUp} 0.8s ease ${(props) => 0.6 + props.index * 0.15}s both;

  @media (max-width: 480px) {
    padding: 22px 18px;
  }
`;

const CardIconBadge = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${(props) => props.$gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 4px 18px ${(props) => props.$shadow};
  flex-shrink: 0;
`;

const CardTitle = styled.h2`
  font-size: clamp(1.1rem, 3vw, 1.4rem);
  color: #fff;
  margin-bottom: 10px;
  font-weight: 700;
`;

const CardText = styled.p`
  font-size: clamp(0.92rem, 2.5vw, 1.05rem);
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.8;
  margin: 0;
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 36px;
  padding: 14px 40px;
  font-size: clamp(1rem, 3vw, 1.2rem);
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #f12711, #f5af19);
  border-radius: 50px;
  text-decoration: none;
  box-sizing: border-box;
  white-space: nowrap;
  line-height: 1;
  cursor: pointer;
  animation: ${fadeInUp} 0.8s ease 1.3s both;
  transition: transform 0.2s;

  &:hover {
    animation: ${pulse} 0.6s ease;
    transform: scale(1.03);
    color: #fff;
    text-decoration: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 320px;
    padding: 14px 24px;
  }
`;

/* ── ページ下部：想いセクション ── */
const Divider = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255,165,0,0.5), transparent);
  margin: 60px auto 0;
`;

const StorySection = styled.section`
  margin-top: 30px;
  max-width: 640px;
  width: 100%;
  padding-top: 10px;
  animation: ${fadeInUp} 0.8s ease 1.5s both;
`;

const StoryLead = styled.p`
  font-size: clamp(1.1rem, 3.2vw, 1.35rem);
  font-weight: 700;
  color: #ffa04c;
  line-height: 2.2;
  margin-bottom: 36px;
  text-align: center;
  animation: ${fireGlow} 5s ease-in-out 2s infinite;
`;

const StoryBody = styled.p`
  font-size: clamp(0.88rem, 2.4vw, 1rem);
  color: rgba(255, 255, 255, 0.72);
  line-height: 2.2;
  margin-bottom: 28px;
  text-align: left;
`;

const StoryEmphasis = styled.span`
  color: rgba(255, 200, 120, 0.9);
  font-weight: 600;
`;

const StoryBreak = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 1.2rem;
  letter-spacing: 0.5em;
  margin: 32px 0;
`;

const StoryHighlight = styled.div`
  font-size: clamp(0.95rem, 2.6vw, 1.1rem);
  font-weight: 700;
  color: #ffd700;
  line-height: 2.2;
  margin: 36px 0;
  padding: 24px;
  border-left: 3px solid rgba(255, 165, 0, 0.5);
  background: rgba(255, 165, 0, 0.05);
  border-radius: 0 10px 10px 0;
  white-space: pre-wrap;
`;

const StoryClosing = styled.p`
  font-size: clamp(0.88rem, 2.4vw, 1rem);
  color: rgba(255, 255, 255, 0.6);
  line-height: 2.2;
  margin-bottom: 16px;
  text-align: center;
`;

const StoryFinal = styled.p`
  font-size: clamp(0.92rem, 2.5vw, 1.05rem);
  color: rgba(255, 255, 255, 0.75);
  line-height: 2.2;
  margin-top: 28px;
  text-align: center;
  font-weight: 600;
`;

const FooterQuote = styled.p`
  margin-top: 50px;
  font-size: clamp(0.85rem, 2.8vw, 1.15rem);
  font-weight: 600;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.3);
  text-align: center;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  width: 100%;
  max-width: 640px;
`;

/* ── カード定義 ── */
const cards = [
  {
    icon: faFire,
    gradient: "linear-gradient(135deg, #f12711 0%, #f5af19 100%)",
    shadow: "rgba(241, 39, 17, 0.35)",
    title: "夢を宣言しよう",
    text: "あなたの目標を世界に向けて宣言。\n言葉にした瞬間、夢は動き出す。",
  },
  {
    icon: faRobot,
    gradient: "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)",
    shadow: "rgba(6, 182, 212, 0.35)",
    title: "AIが無謀度を診断",
    text: "AIがあなたの目標の「無謀度」をスコアリング。\n数値が大きいほど、ぶっ飛んだ夢。\n低くたっていい。まずは一歩。",
  },
  {
    icon: faBullhorn,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    shadow: "rgba(102, 126, 234, 0.35)",
    title: "掲示板で語り合おう",
    text: "目標ごとの掲示板で、応援もツッコミも大歓迎。\nログインなしでもコメント・いいねOK！",
  },
  {
    icon: faTrophy,
    gradient: "linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)",
    shadow: "rgba(255, 215, 0, 0.35)",
    title: "無謀度ランキング",
    text: "全ユーザーの夢がランキングで競演。\nぶっ飛んだ目標ほど上位に。\nあなたの夢は何位？",
  },
];

function Site() {
  const { isLoggedIn } = useUser();

  return (
    <PageWrapper>
      <HeroTitle>無謀な夢を追う、愚か者たちへ</HeroTitle>
      <SubTitle>― 無謀な夢ほど、おもしろい ―</SubTitle>

      <HeroImageWrapper>
        <HeroImage src="/img/GoalHero.png" alt="Arms Crossed" />
      </HeroImageWrapper>

      <CatchCopy>
        {"笑われるくらいがちょうどいい。\nその夢、ここで叫べ。"}
      </CatchCopy>

      {cards.map((c, i) => (
        <Card key={i} index={i}>
          <CardIconBadge $gradient={c.gradient} $shadow={c.shadow}>
            <FontAwesomeIcon icon={c.icon} color="#fff" size="lg" />
          </CardIconBadge>
          <CardTitle>{c.title}</CardTitle>
          <CardText style={{ whiteSpace: "pre-wrap" }}>{c.text}</CardText>
        </Card>
      ))}

      <CtaButton href={isLoggedIn ? "/Register" : "/Login"}>
        {isLoggedIn ? "さっそく目標を宣言する" : "ログインして目標を宣言する"}
      </CtaButton>

      {/* ── 想い ── */}
      <Divider />
      <StorySection>
        <StoryLead>
          せっかく生まれてきたからには、
          <br />
          何かを成し遂げたい。
        </StoryLead>

        <StoryBody>
          でも、毎日過ぎていく日々の中で、
          <br />
          その時の思いは次第に薄れ、
          <br />
          結局何も進まない。
        </StoryBody>

        <StoryBody>
          何かをしようと思っていたのに、
          <br />
          本当は挑戦したいことがあったのに——
          <br />
          <br />
          <StoryEmphasis>「そんなことできるわけない」</StoryEmphasis>
          <br />
          <StoryEmphasis>「リスクが高すぎる」</StoryEmphasis>
          <br />
          <StoryEmphasis>「親が心配するから」</StoryEmphasis>
          <br />
          <StoryEmphasis>「家族がいるから……」</StoryEmphasis>
          <br />
          <br />
          そんな理由を時々考えて、
          <br />
          結局日々に流され、
          <br />
          何もできないでいる自分がいる。
        </StoryBody>

        <StoryBreak>・・・</StoryBreak>

        <StoryBody>
          小さなことから始めようとよく言うけど、
          <br />
          どうしたらいいんだろう？
          <br />
          <br />
          目標は口に出せばいいって言うけど、
          <br />
          頭おかしいって思われるんじゃないか？
          <br />
          結局恥をかくだけじゃないか？
        </StoryBody>

        <StoryBody>
          ダメだ。
          <br />
          結局一人じゃ言い訳して進まない——。
        </StoryBody>

        <StoryBreak>・・・</StoryBreak>

        <StoryHighlight>
          {"いつだって\n「できるわけがない」と言う人がいる。\n\nやったことがないのに、\n自分のことには自信がないのに、\n人のやる事にはなぜか自信を持って\n否定してくる人がいる。\n\nできない理屈を説明して\nばかにする人もいる。\n\nだけど——\nそれを全て跳ね除けて達成したとき、\n\nきっと何もしていない人たちには\n一生見ることができない景色が\n見られる。\n\nそう信じて、\nモチベーションに変えることもできる。"}
        </StoryHighlight>

        <StoryClosing>
          そんな人たちのために、
          <br />
          つたないけれど
          <br />
          目標を宣言できる場を作りました。
        </StoryClosing>

        <StoryClosing>
          無謀な夢と人から笑われながらも、
          <br />
          それを目指す人たちの
          <br />
          役に立てれば嬉しいです。
        </StoryClosing>

        <StoryFinal>
          小さな無謀から大きなものまで、
          <br />
          なんでも自由に目標を立てて、
          <br />
          専用掲示板を自由に使ってください。
        </StoryFinal>
      </StorySection>

      <FooterQuote>無謀な夢を追う、愚か者たちへ</FooterQuote>
    </PageWrapper>
  );
}

export default Site;
